import React, { useState, createContext, useContext } from 'react';

interface Toast {
  id: number;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}

interface ToastContextType {
  showToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

// Toast Context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const id = Date.now();
    const newToast = { id, message, type };
    
    setToasts(prev => [...prev, newToast]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };

  const getToastStyles = (type: 'info' | 'success' | 'warning' | 'error') => {
    const baseStyles = "px-5 py-3 rounded-xl shadow-lg font-medium animate-slide-in max-w-sm md:max-w-md break-words";
    
    switch (type) {
      case 'info':
        return `${baseStyles} bg-primary text-primary-text`;
      case 'success':
        return `${baseStyles} bg-primary text-primary-text`;
      case 'warning':
        return `${baseStyles} bg-accent text-text`;
      case 'error':
        return `${baseStyles} bg-alert text-primary-text`;
      default:
        return `${baseStyles} bg-primary text-primary-text`;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-5 right-5 md:right-5 md:left-auto left-5 z-50 flex flex-col gap-2.5">
        {toasts.map(toast => (
          <div key={toast.id} className={getToastStyles(toast.type)}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const Toast: React.FC = () => {
  return null;
};

export default Toast; 